import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Col, Row, Table, Typography } from 'antd';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton'; // Import IconButton
import { DeleteForeverRounded, ArrowBackIos, ArrowForwardIos } from "@mui/icons-material"; // Import arrow icons
import { useTickerSearchIndex } from "../../modules/Search/useTickerSearchIndex";
import { fetchTickerData } from "../../api/tickersApi";
import { selectAllTickerAttrs } from "../../store/slices/tickers.slice";
import { mergeEtfs } from "../SmaV2/utils/smaUtils";
import TickerChip from "../../components/TickerChip";
import { stringMixin } from "../../utils/strings";


const TickerColumn = ({ stockList }) => {
    const sortedStocks = stockList.sort((a, b) => b.wgt - a.wgt);
    const topStocks = sortedStocks.length > 50 ? sortedStocks.slice(0, 50) : sortedStocks;
    const { tickers } = useTickerSearchIndex(topStocks.map(x => x.symbol));
    const columns = [
        {
            title: "Ticker",
            dataIndex: "symbol",
            key: "symbol",
            render: (symbol) => {
                const ticker = tickers.find(x => x.symbol === symbol.toLowerCase());
                return ticker ? <TickerChip ticker={ticker} /> : symbol;
            }
        },
        {
            title: "Weight",
            dataIndex: "wgt",
            key: "wgt",
            render: (wgt) => stringMixin.pct(wgt)
        }
    ];

    return (
        <Table dataSource={topStocks} columns={columns} pagination={false} />
    );
};

const getTop50StocksFromEtfPortfolio = (portfolio, etfData) => {
    const newEtfData = [];
    for (let k in portfolio) {
        const weight = portfolio[k].wgt;
        if (weight > 0 && etfData.hasOwnProperty(k)) {
            const data = etfData[k];
            if (data.hasOwnProperty('holdings') && Array.isArray(data.holdings)) {
                newEtfData.push({ ...data, weight });
            }
        }
    }
    const mergedData = mergeEtfs(Object.values(newEtfData));
    return mergedData;
};

const XrayPortfolios = ({ portfolios, onRemove, wrap = false }) => {
    const uniqueTickers = [...new Set(portfolios.map(portfolio => Object.keys(portfolio.ptfl)).flat())];
    const { tickers } = useTickerSearchIndex(uniqueTickers);
    const dispatch = useDispatch();
    const etfs = tickers && tickers.filter(ticker => ticker.type === 'etf');
    const [fetched, setFetched] = useState(false);
    const etfData = useSelector(state => selectAllTickerAttrs(state, etfs.map(x => x.symbol), ['symbol', 'name', 'holdings']));
    useEffect(() => {
        if (etfs && etfs.length > 0 &&  Object.values(etfData).some(x => !x.hasOwnProperty('holdings'))) {
            dispatch(fetchTickerData({ symbols: etfs.map(x => x.symbol), fields: ['symbol', 'name', 'holdings'] })).then(() => {
                setFetched(true);
            });
        }
    }, [dispatch, etfs]);

    const mergedPortfoliosData = fetched && etfData && portfolios.map(portfolio => {
        const stocks = getTop50StocksFromEtfPortfolio(portfolio.ptfl, etfData);
        return {
            ...portfolio,
            stocks
        };
    });
    const isReady = mergedPortfoliosData && Array.isArray(mergedPortfoliosData) && mergedPortfoliosData.some(x => x.stocks.length > 0);

    // Scroll function
    const scrollContainerRef = useRef(null);
    const scrollAmount = 300; // Adjust scroll amount as needed

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };
    const handleRemove = useCallback((portfolioKey) => {
        if (onRemove) {
          // Prevent event bubbling
          onRemove(portfolioKey);
        }
      }, [onRemove]);


    return (
        <div style={{ position: 'relative' }}>
            {!wrap && (
                <>
                    {/* Left Scroll Button */}
                    <IconButton
                        onClick={scrollLeft}
                        className={'sticky top-0 z-50'}
                        style={{
                            position: 'absolute',
                            top: '20%',
                            left: 0,
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: '50%',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                        }}
                        aria-label="Scroll Left"
                    >
                        <ArrowBackIos />
                    </IconButton>
                    {/* Right Scroll Button */}
                    <IconButton
                        className={'sticky top-0 z-50'}
                        onClick={scrollRight}
                        style={{
                            position: 'absolute',
                            top: '20%',
                            right: 0,
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: '50%',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                        }}
                        aria-label="Scroll Right"
                    >
                        <ArrowForwardIos />
                    </IconButton>
                </>
            )}
            <div
                ref={scrollContainerRef}
                style={{
                    overflowX: wrap ? 'visible' : 'auto',
                    padding: wrap ? '0' : '0 40px', // Space for buttons when not wrapping
                    whiteSpace: wrap ? 'normal' : 'nowrap',
                    display: 'flex',
                }}
            >
                <Row className={'bordered-rows'} wrap={wrap} style={{ flexWrap: wrap ? 'wrap' : 'nowrap' }}>
                    {isReady && mergedPortfoliosData.filter(x => x.stocks.length > 0).map((portfolio, index) => (
                        <Col key={index}>
                            <Card bordered={false}>
                                <Row className={'bordered round-1 p-3'} justify={'space-between'} align={'middle'}>
                                    <Typography.Text strong>
                                        {portfolio.name}
                                    </Typography.Text>
                                    {onRemove && (
                                        <Button variant={'outlined'} onClick={() => handleRemove(portfolio?.key)}>
                                            <DeleteForeverRounded />
                                        </Button>
                                    )}
                                </Row>
                                <Row className={'p-3'}>
                                    {portfolio?.extra}
                                </Row>
                                <TickerColumn stockList={portfolio.stocks} />
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        </div>
    );
};

export default XrayPortfolios;
